import StarIcon from "@mui/icons-material/Star";
import { MenuItem } from "@mui/material";
import Box from "@mui/material/Box";
import MuiPagination from "@mui/material/Pagination";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import lightGallery from "lightgallery";
import { InitDetail } from "lightgallery/lg-events";
import orderBy from "lodash/orderBy";
import { useEffect, useRef, useState, useCallback } from "react";
import React from "react";
import z from "zod";

import { dayjs } from "@holibob-packages/dayjs";
import { ProductReviewListSort, SortDirection } from "@holibob-packages/graphql-types";
import { ID } from "@holibob-packages/id";
import { Language } from "@holibob-packages/language";
import { AssetUrl } from "@holibob-packages/vault";

import { useProductReviewListQuery } from "../../apiHooks/graphql";
import { ReadMoreContainer } from "../../components/ExpandableContent";
import { LightGalleryImage } from "../../components/LightGalleryImage";
import { ResponsiveImage } from "../../components/ResponsiveImage";
import { Typography } from "../../components/Typography";
import { ProductReviewFragment, ProductReviewImageFragment, ProductReviewSummaryFragment } from "../../gql-request";
import { useLanguage, useNextTranslation } from "../../hooks";
import { ProductExpandButton } from "./ProductExpandButton";
import {
    ProductReviewListWrapper,
    ProductReviewPaginationContainer,
    ProductReviewRatingWrapper,
    ProductReviewSortSelect,
    ProductReviewWrapper,
    RatingItem,
    RatingListWrapper,
    ReviewRatingAverageWrapper,
    ReviewRatingGrid,
    ReviewRatingGridItem,
    StarIconWrapper,
    ProgressBarContainer,
    ProgressBarFill,
    PROGRESS_BAR_FILL_PERCENTAGE_VARIABLE,
    ProductReviewListSettingsWrapper,
    ProductReviewListFilterWrapper,
    ProductReviewListSortWrapper,
    ProductReviewRatingSection,
    ProductReviewListItemWrapper,
    ProductReviewListItemInnerWrapper,
    ProductReviewTitleWrapper,
} from "./ProductReviewLayoutComponents";
import { ProductReviewListSkeleton } from "./ProductReviewSkeleton";
import { ProductRating } from "./ProductReviewsInfoV2";
import { ProductSectionTitle } from "./ProductSectionTitle";
import { ProductSectionWrapperProps } from "./sections/ProductSectionWrapper";

const SortOptionsEnum = z.enum(["published-date-desc", "rating-desc", "rating-asc", "helpfulness-desc"]);
type SortOptionEnum = z.infer<typeof SortOptionsEnum>;
const sortOptions: Record<SortOptionEnum, ProductReviewListSort> = {
    "published-date-desc": { publishedDate: SortDirection.Desc },
    "rating-desc": { rating: SortDirection.Desc },
    "rating-asc": { rating: SortDirection.Asc },
    "helpfulness-desc": { helpfulness: SortDirection.Desc },
};

export type ProductReviewSectionProps = Omit<ProductSectionWrapperProps, "children"> & {
    productId: ID;
    initialData: {
        reviewList: ProductReviewFragment[];
        reviewSummary: ProductReviewSummaryFragment;
        pageCount: number;
    };
};

export function ProductReview(props: ProductReviewSectionProps) {
    const { productId, initialData } = props;
    const [t] = useNextTranslation("product");
    const [ratingFilter, setRatingFilter] = useState<number[]>([1, 2, 3, 4, 5]);
    const [page, setPage] = React.useState(1);
    const [sort, setSort] = React.useState<SortOptionEnum>(SortOptionsEnum.Values["published-date-desc"]);
    const { data, loading } = useProductReviewListQuery({
        variables: {
            productId,
            page,
            pageSize: 4,
            sort: sortOptions[sort],
            filter: { ratingList: ratingFilter },
        },
    });
    const reviewList = data?.productReviewList?.nodes ?? initialData.reviewList;
    const summary = data?.productReviewList?.summary ?? initialData.reviewSummary;
    const pageCount = data?.productReviewList?.pageCount ?? initialData.pageCount;
    const { score, total, histogram } = summary;
    const ratingListUnordered = histogram.nodes;

    const onRatingFilterChange = useCallback((ratingList: number[]) => {
        setRatingFilter(ratingList);
    }, []);

    const onSortChange = useCallback((sortOption: SortOptionEnum) => {
        setSort(sortOption);
    }, []);

    const ratingList = orderBy(ratingListUnordered, "rating", "desc");

    const useSkeleton = loading && page !== 1;

    return (
        <>
            <ProductSectionTitle>{t("label.review")}</ProductSectionTitle>
            <ProductReviewWrapper>
                <ProductReviewRating ratingList={ratingList} score={score} total={total} />
                <ProductReviewListSettings
                    ratingList={ratingList}
                    sort={sort}
                    onRatingFilterChange={onRatingFilterChange}
                    onSortChange={onSortChange}
                />
                {useSkeleton ? <ProductReviewListSkeleton /> : <ProductReviewList reviewList={reviewList} />}
            </ProductReviewWrapper>
            <ProductReviewPagination page={page} pageCount={pageCount} onPageChange={(page) => setPage(page)} />
        </>
    );
}
type ProductReviewSettingsProps = {
    ratingList: Rating[];
    sort: SortOptionEnum;
    onRatingFilterChange: (ratingList: number[]) => void;
    onSortChange: (sortOption: SortOptionEnum) => void;
};
function ProductReviewListSettings({
    ratingList,
    sort,
    onRatingFilterChange: onRatingFilterChange,
    onSortChange,
}: ProductReviewSettingsProps) {
    const [filter, setFilter] = useState(
        Object.fromEntries(ratingList.map(({ rating, count }) => [rating, { isEnabled: count > 0, isActive: true }]))
    );

    const onFilterClick = (rating: number) => {
        setFilter((prevFilter) => {
            const { isEnabled, isActive } = prevFilter[rating];
            return { ...prevFilter, [rating]: { isEnabled, isActive: !isActive } };
        });
    };

    useEffect(() => {
        onRatingFilterChange(
            Object.entries(filter)
                .filter(([, { isEnabled, isActive }]) => isEnabled && isActive)
                .map(([rating]) => parseInt(rating))
        );
    }, [filter, onRatingFilterChange]);

    return (
        <ProductReviewListSettingsWrapper>
            <ProductReviewListFilterWrapper>
                <RatingListWrapper>
                    {ratingList.map(({ rating }, index) => (
                        <React.Fragment key={rating}>
                            <RatingFilterItem
                                rating={rating}
                                isEnabled={filter[rating].isEnabled}
                                isActive={filter[rating].isActive}
                                onClick={() => onFilterClick(rating)}
                            />
                            {index < ratingList.length - 1 && <Separator>|</Separator>}
                        </React.Fragment>
                    ))}
                </RatingListWrapper>
            </ProductReviewListFilterWrapper>
            <ProductReviewListSortWrapper>
                <SortOptionSelect sort={sort} onSortChange={onSortChange} />
            </ProductReviewListSortWrapper>
        </ProductReviewListSettingsWrapper>
    );
}

type RatingFilterItemProps = {
    rating: number;
    isEnabled: boolean;
    isActive: boolean;
    onClick: () => void;
};

function RatingFilterItem({ rating, isEnabled, isActive, onClick }: RatingFilterItemProps) {
    const theme = useTheme();
    const iconFill = isEnabled ? (isActive ? theme.palette.primary.main : theme.palette.common.white) : "transparent";

    return (
        <RatingItem data-enabled={isEnabled} onClick={onClick}>
            <Typography variant="body" size="small" data-ref="rating">
                {rating}
            </Typography>
            <StarIconWrapper>
                <StarIcon sx={{ fill: iconFill }} />
            </StarIconWrapper>
        </RatingItem>
    );
}

type SortOptionSelectProps = {
    sort: SortOptionEnum;
    onSortChange: (sort: SortOptionEnum) => void;
};

function SortOptionSelect({ sort, onSortChange }: SortOptionSelectProps) {
    const [t] = useNextTranslation("product");
    const onSortOptionChange = (value: SortOptionEnum) => {
        onSortChange(value);
    };
    const sortOptionList: { label: string; value: SortOptionEnum }[] = [
        { label: t("review.sort.mostRecent"), value: "published-date-desc" },
        { label: t("review.sort.highestRating"), value: "rating-desc" },
        { label: t("review.sort.lowestRating"), value: "rating-asc" },
        { label: t("review.sort.mostHelpful"), value: "helpfulness-desc" },
    ];
    const selectedOption = sortOptionList.find((x) => x.value === sort);
    return (
        <ProductReviewSortSelect
            variant="outlined"
            value={sort}
            onChange={(event) => onSortOptionChange(event.target.value as SortOptionEnum)}
            size="small"
            renderValue={(value) => {
                const option = sortOptionList.find((x) => x.value === value);
                return option?.label;
            }}
            label={
                <Typography variant="body" size="small">
                    {selectedOption?.label}
                </Typography>
            }
        >
            {sortOptionList.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    <Typography variant="body" size="small">
                        {option.label}
                    </Typography>
                </MenuItem>
            ))}
        </ProductReviewSortSelect>
    );
}

type ProductReviewRatingProps = {
    score: number;
    total: number;
    ratingList: Rating[];
};

function ProductReviewRating({ score, total, ratingList }: ProductReviewRatingProps) {
    const ratingMax = Math.max(...ratingList.map((rating) => rating.rating));

    return (
        <ProductReviewRatingWrapper>
            <ProductReviewRatingSection>
                <ProductReviewRatingAverage ratingAverage={score} reviewCount={total} ratingMax={ratingMax} />
            </ProductReviewRatingSection>
            <ProductReviewRatingSection data-ref="rating-details">
                <ProductReviewRatingDetails ratingList={ratingList} totalReviewCount={total} />
            </ProductReviewRatingSection>
        </ProductReviewRatingWrapper>
    );
}

type ProductReviewRatingAverageProps = { ratingAverage: number; ratingMax: number; reviewCount: number };

function ProductReviewRatingAverage({ ratingAverage, ratingMax, reviewCount }: ProductReviewRatingAverageProps) {
    const [t] = useNextTranslation("product");

    return (
        <ReviewRatingAverageWrapper>
            <Typography variant="title">{t("review.ratingAverage")}</Typography>
            <Box display="flex" gap={1} alignItems="center">
                <Typography variant="headline" size="large">
                    {ratingAverage}
                </Typography>
                <Box display="flex" position="relative" gap={1} top={3}>
                    <Typography variant="headline" size="small">
                        / {ratingMax}
                    </Typography>
                </Box>
            </Box>
            <ProductRating reviewRating={ratingAverage} reviewCount={reviewCount} withCount={false} />
            <Box display="flex" alignItems="center">
                <Typography variant="body">{t("review.travellerRating", { reviewCount })}</Typography>
            </Box>
        </ReviewRatingAverageWrapper>
    );
}

type Rating = { rating: number; count: number };

type ProductReviewRatingDetailsProps = { ratingList: Rating[]; totalReviewCount: number };

const ProductReviewRatingDetails = ({ ratingList, totalReviewCount }: ProductReviewRatingDetailsProps) => {
    return (
        <ReviewRatingGrid>
            {ratingList.map(({ rating, count }) => (
                <ReviewRatingGridRow
                    key={rating}
                    rating={rating}
                    reviewCount={count}
                    percentage={(count / totalReviewCount) * 100}
                />
            ))}
        </ReviewRatingGrid>
    );
};

type ReviewRatingGridRowProps = { rating: number; reviewCount: number; percentage: number };

function ReviewRatingGridRow({ rating, reviewCount, percentage }: ReviewRatingGridRowProps) {
    return (
        <>
            <ReviewRatingGridItem>
                <Box display="flex" flex={1}>
                    <Typography variant="body">{rating}</Typography>
                </Box>
                <Box display="flex">
                    <ReviewStarIcon />
                </Box>
            </ReviewRatingGridItem>
            <ReviewRatingGridItem>
                <ProgressBarContainer>
                    <ProgressBarFill style={{ [PROGRESS_BAR_FILL_PERCENTAGE_VARIABLE.toString()]: `${percentage}%` }} />
                </ProgressBarContainer>
            </ReviewRatingGridItem>
            <ReviewRatingGridItem>{<Typography variant="body">{reviewCount}</Typography>}</ReviewRatingGridItem>
        </>
    );
}

type ProductReviewListProps = {
    reviewList: ProductReviewFragment[];
};

function ProductReviewList({ reviewList }: ProductReviewListProps) {
    const language = useLanguage();
    return (
        <ProductReviewListWrapper>
            {reviewList.map((productReview) => (
                <ProductReviewListItem key={productReview.id} productReview={productReview} language={language} />
            ))}
        </ProductReviewListWrapper>
    );
}

function formatProductReviewPublishedDate(publishedDate: string, language: Language) {
    const intlDateTime = new Intl.DateTimeFormat(language.code, { year: "numeric", month: "short" });
    return intlDateTime.format(dayjs(publishedDate).toDate());
}

type ReviewListItemProps = {
    productReview: ProductReviewFragment;
    language: Language;
};

function ProductReviewListItem({ productReview, language }: ReviewListItemProps) {
    const [t] = useNextTranslation("product");
    const { authorName, publishedDate, imageList, ownerResponse } = productReview;
    const formattePublisheddDate = formatProductReviewPublishedDate(publishedDate, language);
    const subtitle = `${authorName}, ${formattePublisheddDate}`;

    return (
        <ProductReviewListItemWrapper>
            <ReadMoreContainer threshold={250} ExpandButtonComponent={ProductReviewReadMoreButton}>
                <ProductReviewListItemInnerWrapper>
                    <ProductReviewTitleWrapper>
                        <ProductRating reviewRating={productReview.rating} withCount={false} />
                        <Typography variant="title" fontWeight="medium">
                            {productReview.title}
                        </Typography>
                    </ProductReviewTitleWrapper>
                    <Typography variant="body" size="small">
                        {subtitle}
                    </Typography>
                    <Typography variant="body" color="subtle">
                        {productReview.content}
                    </Typography>
                    <ProductReviewImageList imageList={imageList.nodes} />
                    {ownerResponse && (
                        <ProductReviewListItemResponseWrapper>
                            <Typography variant="body" size="small">
                                {t("review.ownerResponseTitle", {
                                    date: formatProductReviewPublishedDate(ownerResponse.publishedDate, language),
                                })}
                            </Typography>
                            <Typography variant="body" color="subtle">
                                {ownerResponse.content}
                            </Typography>
                        </ProductReviewListItemResponseWrapper>
                    )}
                </ProductReviewListItemInnerWrapper>
            </ReadMoreContainer>
        </ProductReviewListItemWrapper>
    );
}

type ProductReviewImageListProps = {
    imageList: ProductReviewImageFragment[];
};

function ProductReviewImageList({ imageList }: ProductReviewImageListProps) {
    const ref = useRef<ReturnType<typeof lightGallery> | undefined>(undefined);
    function onImageGalleryInit(detail: InitDetail) {
        ref.current = detail.instance;
    }
    const openImage = (index: number) => {
        const instance = ref.current;
        if (instance) {
            instance.openGallery(index);
        }
    };

    const assetUrlList = imageList.map(({ uri }) => AssetUrl.fromString(uri).unwrap());

    return (
        <ProductReviewImageListWrapper>
            {assetUrlList.map((x, index) => (
                <ProductReviewImageWrapper key={index}>
                    <ResponsiveImage
                        alt="review-image"
                        src={x}
                        onClick={() => openImage(index)}
                        width={ProductReviewImageWidth}
                        height={ProductReviewImageHeight}
                    />
                </ProductReviewImageWrapper>
            ))}
            <LightGalleryImage assetUrlList={assetUrlList} onInit={onImageGalleryInit} />
        </ProductReviewImageListWrapper>
    );
}

type ProductReviewPaginationProps = {
    page: number;
    pageCount: number;
    onPageChange: (page: number) => void;
};
const ProductReviewPagination = (props: ProductReviewPaginationProps) => {
    const { page, pageCount, onPageChange } = props;

    if (pageCount < 2) return null;

    return (
        <ProductReviewPaginationContainer>
            <MuiPagination color="primary" page={page} count={pageCount} onChange={(_, page) => onPageChange(page)} />
        </ProductReviewPaginationContainer>
    );
};

const ReviewStarIcon = styled(StarIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
    fill: theme.palette.primary.main,
    width: 16,
    height: 16,
}));

const ProductReviewListItemResponseWrapper = styled("div")(({ theme }) => ({
    backgroundColor: "#F6F6F6",
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const ProductReviewReadMoreButton = styled(ProductExpandButton)(({ theme }) => ({
    "&": {
        position: "relative",
        top: `-${theme.spacing(0.75)}`,
        left: `-${theme.spacing(1)}`,
    },
}));

const ProductReviewImageHeight = 70;
const ProductReviewImageWidth = 100;

const ProductReviewImageListWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(1),
}));

const ProductReviewImageWrapper = styled("div")(({ theme }) => ({
    background: "transparent",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    opacity: 1,
    transition: theme.transitions.create("opacity", {
        duration: theme.transitions.duration.short,
    }),
    "&:hover": {
        opacity: 0.8,
    },
}));

export const Separator = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));
